import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Ban, Send, Eye, ImageIcon, Building2 } from "lucide-react";
import AuthedImg from "../components/AuthedImg";

const SECTION_META = [
    { key: "checklist", label: "Checklist completion" },
    { key: "service_requests", label: "Service requests" },
    { key: "attendance", label: "Attendance" },
    { key: "consumables", label: "Consumables & deliveries" },
    { key: "usage_logs", label: "Beyond-hours & DG usage" },
    { key: "compliance", label: "Compliance visits + status" },
    { key: "special_jobs", label: "Special jobs completed" },
    { key: "photos", label: "Selected photos" },
];

export default function ReportEditorPage() {
    const { reportId } = useParams();
    const [r, setR] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/reports/${reportId}`); setR(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [reportId]);
    useEffect(() => { load(); }, [load]);

    async function patch(payload) {
        try {
            const { data } = await api.patch(`/reports/${reportId}`, payload);
            setR(data);
        } catch (e) { toast.error(apiError(e)); }
    }

    async function publish() {
        try {
            const { data } = await api.post(`/reports/${reportId}/publish`);
            toast.success("Published");
            load();
            // copy the link
            const url = `${window.location.origin}${data.url}`;
            navigator.clipboard.writeText(url).then(() => toast.success("Public link copied")).catch(() => {});
        } catch (e) { toast.error(apiError(e)); }
    }

    async function unpublish() {
        if (!window.confirm("Unpublish this report? The public link will stop working.")) return;
        try { await api.post(`/reports/${reportId}/unpublish`); toast.success("Unpublished"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    if (loading || !r) return <div className="mx-auto max-w-4xl px-4 py-10"><div className="h-40 rounded-xl bg-white border border-gray-100 animate-pulse" /></div>;
    const d = r.data;
    const enabled = r.sections_enabled || {};
    const isPublished = r.status === "PUBLISHED";
    const isLocked = isPublished;

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/admin/reports" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355]"><ArrowLeft className="w-4 h-4" /> All reports</Link>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> {r.site_name} · {r.month}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Monthly report</h1>
                    <div className="mt-1 text-xs text-gray-500">Status: <span className="font-semibold text-[#1E3355]">{r.status}</span>{isPublished && <> · v{r.version} · <a href={`/r/${r.published_token}`} target="_blank" rel="noreferrer" className="text-emerald-700 underline">/r/{r.published_token.slice(0, 12)}…</a></>}</div>
                </div>
                <div className="flex gap-2">
                    {isPublished && <a href={`/r/${r.published_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-emerald-600 text-emerald-700 px-3 py-2 text-sm font-semibold hover:bg-emerald-50" data-testid="report-view-public"><Eye className="w-4 h-4" /> View public</a>}
                    {!isPublished && <button onClick={publish} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-sm font-semibold" data-testid="report-publish"><Send className="w-4 h-4" /> Publish</button>}
                    {isPublished && <button onClick={unpublish} className="inline-flex items-center gap-1 rounded-md bg-[#E02B2B] hover:bg-red-700 text-white px-3 py-2 text-sm font-semibold" data-testid="report-unpublish"><Ban className="w-4 h-4" /> Unpublish</button>}
                </div>
            </div>

            {isLocked && (
                <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
                    Published reports are locked. Unpublish first if you need to edit.
                </div>
            )}

            <div className="mt-6 space-y-4">
                <Card title="Intro note" testId="report-section-intro">
                    <textarea disabled={isLocked} rows={3} value={r.intro_note || ""} onChange={(e) => setR({ ...r, intro_note: e.target.value })} onBlur={(e) => patch({ intro_note: e.target.value })} placeholder="Optional cover message to the client…" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355]" data-testid="report-intro-input" />
                </Card>

                <Card title="Sections" testId="report-section-toggles">
                    <ul className="divide-y divide-gray-100">
                        {SECTION_META.map((s) => (
                            <li key={s.key} className="py-2.5 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" disabled={isLocked} checked={!!enabled[s.key]} onChange={(e) => { const ne = { ...enabled, [s.key]: e.target.checked }; setR({ ...r, sections_enabled: ne }); patch({ sections_enabled: ne }); }} data-testid={`section-toggle-${s.key}`} />
                                        <span className="font-medium text-[#1E3355]">{s.label}</span>
                                    </label>
                                </div>
                                <span className="text-[11px] text-gray-500">{s.key}</span>
                            </li>
                        ))}
                    </ul>
                </Card>

                {enabled.checklist && <ChecklistSection rows={d.checklist_rows} />}
                {enabled.service_requests && <SRSection rows={d.sr_rows} />}
                {enabled.attendance && <AttendanceSection rows={d.attendance} />}
                {enabled.consumables && <ConsumablesSection deliveries={d.deliveries} stockouts={d.stockouts} />}
                {enabled.usage_logs && <UsageSection usage={d.usage} />}
                {enabled.compliance && <ComplianceSection items={d.compliance_status} visits={d.compliance_visits} />}
                {enabled.special_jobs && <SpecialJobsSection summary={d.special_jobs} />}
                {enabled.photos && <PhotosSection photos={d.photos} selected={r.photo_ids || []} onChange={(ids) => { setR({ ...r, photo_ids: ids }); patch({ photo_ids: ids }); }} disabled={isLocked} />}
            </div>
        </div>
    );
}

function Card({ title, children, testId }) {
    return (<div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 sm:p-5" data-testid={testId}><div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">{title}</div>{children}</div>);
}

function ChecklistSection({ rows }) {
    return (
        <Card title="Checklist completion" testId="report-preview-checklist">
            {rows.length === 0 ? <Empty /> :
            <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="text-[10px] text-gray-500 text-left uppercase tracking-wider"><tr><th>Date</th><th>Cycle</th><th>Theme</th><th>Tasks</th><th>Critical</th><th>Escalations</th><th>Sign-off</th><th>Manager</th></tr></thead>
            <tbody className="divide-y divide-gray-100">{rows.map((r) => (
                <tr key={r.id}><td className="py-1.5">{fmtDate(r.date)}</td><td>{r.cycle_day}</td><td className="truncate max-w-[180px]">{r.theme}</td><td>{r.done}/{r.total}</td><td>{r.crit_done}/{r.crit_total}</td><td>{r.escalations}</td><td>{r.signoff_time ? r.signoff_time.slice(11, 16) : "—"}</td><td>{r.manager_name}</td></tr>
            ))}</tbody></table></div>}
        </Card>
    );
}

function SRSection({ rows }) {
    return (
        <Card title="Service requests" testId="report-preview-sr">
            {rows.length === 0 ? <Empty /> :
            <ul className="divide-y divide-gray-100 text-xs">{rows.map((r) => (
                <li key={r.id} className="py-2 flex items-start justify-between gap-2">
                    <div><div className="font-semibold text-[#1E3355]">{r.category}</div><div className="text-gray-600 truncate max-w-md">{r.description}</div></div>
                    <div className="text-right text-gray-500 whitespace-nowrap">{r.status} · ack {r.response_minutes ? `${Math.round(r.response_minutes / 60)}h` : "—"} · close {r.resolution_minutes ? `${Math.round(r.resolution_minutes / 60)}h` : "—"}</div>
                </li>
            ))}</ul>}
        </Card>
    );
}

function AttendanceSection({ rows }) {
    return (
        <Card title="Attendance" testId="report-preview-attendance">
            {rows.length === 0 ? <Empty /> :
            <table className="w-full text-xs"><thead className="text-[10px] text-gray-500 text-left uppercase tracking-wider"><tr><th>Role</th><th>Present</th><th>Absent</th><th>Cover</th><th>Violations</th></tr></thead>
            <tbody className="divide-y divide-gray-100">{rows.map((r) => (
                <tr key={r.role}><td className="py-1.5 font-semibold">{r.role}</td><td>{r.present}</td><td>{r.absent}</td><td>{r.cover_arranged}</td><td className={r.violations > 0 ? "text-[#E02B2B] font-semibold" : ""}>{r.violations}</td></tr>
            ))}</tbody></table>}
        </Card>
    );
}

function ConsumablesSection({ deliveries, stockouts }) {
    return (
        <Card title="Consumables & deliveries" testId="report-preview-consumables">
            <div className="text-[11px] font-semibold text-gray-500 mb-1">Delivered this month</div>
            {deliveries.length === 0 ? <Empty /> : <ul className="text-xs mb-3 space-y-0.5">{deliveries.map((d, i) => <li key={i}>· {d.item_name} — qty {d.qty_received} · {fmtDate(d.delivered_at)}</li>)}</ul>}
            <div className="text-[11px] font-semibold text-gray-500 mt-3 mb-1">Stockout events</div>
            {stockouts.length === 0 ? <div className="text-xs text-gray-500">None. </div> : <ul className="text-xs space-y-0.5">{stockouts.map((s) => <li key={s.id} className={s.resolved_at ? "" : "text-[#E02B2B]"}>· {s.item_name} — {fmtDate(s.created_at)} {s.resolved_at ? `(resolved ${fmtDate(s.resolved_at)})` : "(open)"}</li>)}</ul>}
        </Card>
    );
}

function UsageSection({ usage }) {
    return (
        <Card title="Beyond-hours & DG usage" testId="report-preview-usage">
            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div className="rounded-md bg-[#F4F6F9] p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Beyond-hours total</div><div className="font-display font-bold text-[#1E3355] text-lg">{usage.beyond_hours_total}h</div></div>
                <div className="rounded-md bg-[#F4F6F9] p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">DG runtime total</div><div className="font-display font-bold text-[#1E3355] text-lg">{usage.dg_total}h</div></div>
            </div>
            {usage.beyond_hours_entries.length === 0 && usage.dg_entries.length === 0 && <Empty />}
        </Card>
    );
}

function ComplianceSection({ items, visits }) {
    return (
        <Card title="Compliance status + visits" testId="report-preview-compliance">
            <div className="text-[11px] font-semibold text-gray-500 mb-1">Current status</div>
            <ul className="text-xs mb-3 space-y-0.5">{items.map((c) => <li key={c.id}>· {c.name} — <span className={c.status === "OVERDUE" ? "text-[#E02B2B] font-semibold" : c.status === "DUE_SOON" ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold"}>{c.status}</span> · next {fmtDate(c.next_due_date)}</li>)}</ul>
            <div className="text-[11px] font-semibold text-gray-500 mb-1">Visits this month</div>
            {visits.length === 0 ? <div className="text-xs text-gray-500">No visits logged this month.</div> : <ul className="text-xs space-y-0.5">{visits.map((v) => <li key={v.id}>· {fmtDate(v.visit_date)} · {v.vendor || "—"} · logged by {v.logged_by_name}</li>)}</ul>}
        </Card>
    );
}

function SpecialJobsSection({ summary }) {
    const s = summary || { total_jobs: 0, done: 0, verified: 0, carried_over: 0, notable: [] };
    return (
        <Card title="Special jobs completed" testId="report-preview-special_jobs">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="special-jobs-counts">
                <Stat label="Total jobs" value={s.total_jobs} />
                <Stat label="Done" value={s.done} tone="ok" />
                <Stat label="Verified" value={s.verified} tone="ok" />
                <Stat label="Carried over" value={s.carried_over} tone={s.carried_over > 0 ? "warn" : "neutral"} />
            </div>
            <div className="text-[11px] font-semibold text-gray-500 mb-1">Notable jobs</div>
            {(!s.notable || s.notable.length === 0) ? <div className="text-xs text-gray-500">No high-priority notable items this month.</div> : (
                <ul className="text-xs space-y-0.5" data-testid="special-jobs-notable">
                    {s.notable.map((j, i) => (
                        <li key={i}>· {fmtDate(j.date)} · {j.description} · {j.assignee} {j.verified ? <span className="text-emerald-700 font-semibold">· verified</span> : <span className="text-amber-700">· awaiting verify</span>}</li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

function Stat({ label, value, tone = "neutral" }) {
    const cls = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-[#1E3355]";
    return (
        <div className="rounded-md border border-gray-100 bg-[#F4F6F9] px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
            <div className={`font-display text-xl font-bold ${cls}`}>{value ?? 0}</div>
        </div>
    );
}


function PhotosSection({ photos, selected, onChange, disabled }) {
    function toggle(id) { onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]); }
    return (
        <Card title="Photos" testId="report-preview-photos">
            {photos.length === 0 ? <Empty /> :
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photos.map((p) => (
                    <button type="button" key={p.id} disabled={disabled} onClick={() => toggle(p.id)} className={`relative aspect-square rounded-md overflow-hidden border-2 ${selected.includes(p.id) ? "border-[#1E3355]" : "border-gray-200"}`} data-testid={`photo-toggle-${p.id}`}>
                        <AuthedImg src={`/photos/${p.id}/blob`} alt="" className="w-full h-full object-cover" />
                        {selected.includes(p.id) && <div className="absolute top-1 right-1 bg-[#1E3355] text-white rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5" /></div>}
                        <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">{fmtDate(p.created_at)}</div>
                    </button>
                ))}
            </div>}
            <div className="text-[11px] text-gray-500 mt-2">{selected.length} photo{selected.length !== 1 ? "s" : ""} selected</div>
        </Card>
    );
}

function Empty() { return <div className="text-xs text-gray-500">No data for this section this month.</div>; }
